import { useCallback, useMemo } from "react";
import { OverviewType, useAppDispatch, useAppSelector } from "../../app";
import useBulkAction from "./useBulkAction";
import { DocumentSetData } from "../interfaces/documentSet";
import {
  resetSelectedRows,
  updateSelectedRows,
} from "../store/documentSetSlice";
import { Status } from "../../common/status/status";
import { ADMIN_ROLE_ID } from "../../common/utilities/env";

const useBulkActionHeader = (documentSets: Array<DocumentSetData>) => {
  const { selectedRows } = useBulkAction();
  const { overviewType } = useAppSelector((state) => state.documentSetsReducer);
  const user = useAppSelector((state) => state.userReducer.user);

  const dispatch = useAppDispatch();

  const isAdmin = useMemo(() => {
    return user?.access?.includes(ADMIN_ROLE_ID) ?? false;
  }, [user]);

  const selectableDocuments = useMemo(() => {
    // Archived view is admin-only
    if (overviewType === OverviewType.Archived && !isAdmin) {
      return [];
    }
    return documentSets.filter((doc) => ![Status.Remove].includes(doc.status));
  }, [documentSets, overviewType, isAdmin]);

  const isAllSelected = useMemo(() => {
    return (
      selectedRows.length === selectableDocuments.length &&
      selectableDocuments.length > 0
    );
  }, [selectedRows, selectableDocuments]);

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.length === selectableDocuments.length) {
      dispatch(resetSelectedRows());
    } else {
      const ids = selectableDocuments.map((row) => row.id);
      dispatch(updateSelectedRows(ids));
    }
  }, [dispatch, selectableDocuments, selectedRows]);

  return {
    selectedRows,
    isAllSelected,
    toggleSelectAll,
  };
};

export default useBulkActionHeader;
